import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Paper,
  Snackbar,
  Toolbar,
  Typography,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
export default function FeeApprovalAdmin() {
  const base = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]),
    [selected, setSelected] = useState([]),
    [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ open: false, message: "", severity: "success" });
  const [proofOpen, setProofOpen] = useState(false);
  const [proofUrl, setProofUrl] = useState("");

  const handleOpenProof = (path) => {
    setProofUrl(`${base}${path}`);
    setProofOpen(true);
  };

  const handleCloseProof = () => {
    setProofOpen(false);
    setProofUrl("");
  };
  const load = async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const r = await axios.get(`${base}/api/fee-approvals/${user.client_id}/${user.college_id}`);
      setRows(
        (r.data?.data || [])
          .filter((x) => String(x.workflow_status || "").toUpperCase() !== "COMPLETED")
          .map((x) => ({ ...x, id: String(x.approve_id) }))
      );
    } catch (e) {
      setNotice({
        open: true,
        message: e.response?.data?.message || "Unable to load approvals",
        severity: "error",
      });
    } finally {
      if (!silent) setBusy(false);
    }
  };
  useEffect(() => {
    load();
    const refreshTimer = window.setInterval(() => load(true), 3000);
    return () => window.clearInterval(refreshTimer);
  }, []);
  const update = async (action) => {
    if (!selected.length)
      return setNotice({
        open: true,
        message: "Select at least one approval request, Click Checkbox",
        severity: "warning",
      });
    setBusy(true);
    try {
      const r = await axios.patch(
        `${base}/api/fee-approvals/${user.client_id}/${user.college_id}/${action}`,
        { approve_ids: selected, modified_by: user.user_email || "" }
      );
      setNotice({ open: true, message: r.data.message, severity: "success" });
      setSelected([]);
      await load();
    } catch (e) {
      setNotice({
        open: true,
        message: e.response?.data?.message || `Unable to ${action}`,
        severity: "error",
      });
    } finally {
      setBusy(false);
    }
  };
  const cols = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: "student_rollno", headerName: "Roll No", width: 180 },
      { field: "student_name", headerName: "Student Name", minWidth: 280, flex: 1 },
      { field: "fee_head_name", headerName: "Fee Book", minWidth: 380, flex: 1 },
      {
        field: "payable_details",
        headerName: "Payable Details",
        minWidth: 300,
        flex: 1,
        renderCell: (params) => (
          <div>
            <strong>Date:</strong>{" "}
            {params.row.payable_date
              ? new Date(params.row.payable_date).toLocaleDateString("en-IN")
              : "-"}
            <br />
            <strong>Fine:</strong> ₹{params.row.fine_amount || 0}
            <br />
            <strong>Remarks:</strong> {params.row.remarks || "-"}
          </div>
        ),
      },
      {
        field: "approval_proof_path",
        headerName: "Proof",
        width: 100,
        renderCell: ({ value }) =>
          value ? (
            <Button
              size="medium"
              variant="contained"
              sx={{
                backgroundColor: "#4caf50",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#388e3c",
                },
              }}
              onClick={() => handleOpenProof(value)}
            >
              View
            </Button>
          ) : (
            "-"
          ),
      },
      {
        field: "workflow_status",
        headerName: "Status",
        width: 125,
        renderCell: ({ value }) => (
          <Chip
            size="medium"
            label={value}
            color={
              value === "APPROVED"
                ? "success"
                : value === "REJECTED"
                ? "error"
                : value === "COMPLETED"
                ? "info"
                : "warning"
            }
          />
        ),
      },
    ],
    [base]
  );
  return (
    <Box p={2} pb={6}>
      <Paper>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            FEE ALLOWED APPROVAL
          </Typography>
          <ButtonGroup>
            <Button
              variant="contained"
              disabled={busy}
              onClick={() => update("approve")}
              sx={{
                backgroundColor: "#2e7d32",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#1b5e20", color: "#fff" },
                "&.Mui-disabled": { backgroundColor: "#a5d6a7", color: "#fff" },
              }}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={busy}
              onClick={() => update("reject")}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                fontWeight: 700,
                "&:hover": { backgroundColor: "#b71c1c", color: "#fff" },
                "&.Mui-disabled": { backgroundColor: "#ef9a9a", color: "#fff" },
              }}
            >
              Reject
            </Button>
          </ButtonGroup>
        </Toolbar>
        <DataGrid
          autoHeight
          checkboxSelection
          rows={rows}
          columns={cols}
          loading={busy}
          rowHeight={80}
          selectionModel={selected}
          onSelectionModelChange={setSelected}
          pageSize={pageSize}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
        />
        <Dialog open={proofOpen} onClose={handleCloseProof} maxWidth="lg" fullWidth>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Approval Proof
            <IconButton
              onClick={handleCloseProof}
              size="medium"
              sx={{
                color: "error.main",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {proofUrl?.toLowerCase().endsWith(".pdf") ? (
              <iframe
                src={proofUrl}
                title="PDF Proof"
                width="100%"
                height="600px"
                style={{ border: "none" }}
              />
            ) : (
              <img
                src={proofUrl}
                alt="Proof"
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseProof}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={() => setNotice({ ...notice, open: false })}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          variant="filled"
          severity={notice.severity}
          onClose={() => setNotice({ ...notice, open: false })}
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
